import React from 'react';
import { useTranslation } from 'react-i18next';
import { ConsentDialog } from 'components/PaymentConsent/ConsentDialog';
import { GenericErrorModal } from 'components/PaymentConsent/GenericErrorModal';
import { SucceedPaymentSnackbar } from 'components/PaymentConsent/SuccessSnackbar';
import { AddPaymentDialog } from './AddPaymentDialog';
import { PaymentMethod } from './PaymentMethod';
import { useTypedSelector, useAppDispatch } from 'combineReducers';
import { fetchAllPendingConnections } from 'actions/connections';
import { updateAutoAccept, clearAutoAcceptErrors } from 'actions/autoAccept';
import { updateAutoPay } from 'actions/autoPay';
import { fetchPaymentMethods, fetchConnectionsBillingPreview } from 'actions/paymentMethods';
import { cleanInsufficientFundsError, cleanBillingError } from 'actions/billingErrors';
import { ICompany } from 'interfaces/index';
import { ConnectionsBillingPreviewPayload } from 'interfaces/paymentMethods';
import { IPendingConnection, AutoConnectStatus } from 'interfaces';
import { showErrorPopUp } from 'helpers/overlayErrors';
import { ConnectionType } from 'helpers/enums';

interface Props {
  open: boolean;
  onClose(): void;
}

enum PaymentOptions {
  EnableAutoConnect = 'EnableAutoConnect',
  AddPaymentMethodOnly = 'AddPaymentMethodOnly'
}

export const ConsentDialogContainer: React.FC<Props> = ({ open, onClose }) => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const organizationDetails = useTypedSelector(state => state.organizationDetails.organizationDetails);
  const { status } = useTypedSelector(state => state.autoAccept);
  const { userInfo } = useTypedSelector(state => state.userInfo);
  const { billingSystemError } = useTypedSelector(state => state.billingErrors);
  const [incomingConnections, setIncomingConnections] = React.useState<IPendingConnection[]>([]);
  const [pendingConnections, setPendingConnections] = React.useState<IPendingConnection[]>([]);
  const [loading, setLoading] = React.useState<boolean>(false);
  const [option, setOption] = React.useState<PaymentOptions>(PaymentOptions.EnableAutoConnect);
  const [showRequiresPaymentPage, setShowRequiresPaymentPage] = React.useState<boolean>(false);
  const [showPaymentPage, setShowPaymentPage] = React.useState<boolean>(false);
  const [showError, setShowError] = React.useState<boolean>(false);
  const [showSuccessSnackbar, setShowSuccessSnackbar] = React.useState<boolean>(false);

  const handleHideRequiresPaymentPage = () => setShowRequiresPaymentPage(false);
  const handleHidePaymentPage = () => {
    setShowPaymentPage(false);
    dispatch(cleanInsufficientFundsError());
  };

  const handleUpdateAutoConnect = (status: boolean) => {
    const data = { status: status };

    organizationDetails &&
      dispatch(
        updateAutoAccept({
          organizationId: organizationDetails.aionOrganizationId,
          status: data,
          afterSuccess: () => {
            if (data.status) {
              setShowSuccessSnackbar(true);
            }
          }
        })
      );
  };

  const handleFetchPaymentMethods = () => {
    organizationDetails &&
      dispatch(
        fetchPaymentMethods({
          companyId: organizationDetails.companyId
        })
      );
  };

  const handleClearAutoAcceptErrors = () => {
    organizationDetails && dispatch(clearAutoAcceptErrors(Number(organizationDetails.companyId)));
  };

  const handleUpdateAutoPayProperty = (value: boolean, consentValue: AutoConnectStatus) => {
    if (userInfo && organizationDetails) {
      dispatch(
        updateAutoPay({
          companyId: Number(organizationDetails.companyId),
          organizationId: organizationDetails.aionOrganizationId,
          email: userInfo.emailAddress ?? '',
          consentValue,
          value,
          afterSuccess: () => {
            handleFetchPaymentMethods();
          }
        })
      );
    }
  };

  const handleAfterSuccessPaymentAdd = () => {
    const paymentMethodOnlyChosen = option === PaymentOptions.AddPaymentMethodOnly;
    const enableAutoConnectChosen = option === PaymentOptions.EnableAutoConnect;

    onClose();
    if (enableAutoConnectChosen) {
      handleUpdateAutoConnect(true);
      handleUpdateAutoPayProperty(true, AutoConnectStatus.InitialSelectionAutoConnect);
    } else if (paymentMethodOnlyChosen) {
      status && handleUpdateAutoConnect(false);

      handleUpdateAutoPayProperty(false, AutoConnectStatus.InitialSelectionPaymentMethodOnly);
      handleClearAutoAcceptErrors();
      setShowSuccessSnackbar(true);
    }
  };

  const handleCloseErrorDialog = () => {
    setShowError(false);
    dispatch(cleanBillingError());
  };

  const fetchAllPendingConnectionsAction = React.useCallback(async () => {
    if (!organizationDetails) {
      return [];
    }
    const pendingConnectionsPayload = await dispatch(
      fetchAllPendingConnections(organizationDetails.aionOrganizationId)
    );

    return (pendingConnectionsPayload as unknown) as IPendingConnection[];
  }, [dispatch, organizationDetails]);

  const fetchAllPendingConnectionsCallback = React.useCallback(async () => {
    if (!organizationDetails) {
      return [];
    }
    setLoading(true);
    const allPendingConnections = await fetchAllPendingConnectionsAction();
    setPendingConnections([...allPendingConnections]);
    setLoading(false);

    const incomingConnections = allPendingConnections.filter(
      connection => connection.requestType === ConnectionType.Incoming
    );

    setIncomingConnections([...incomingConnections]);

    return allPendingConnections;
  }, [fetchAllPendingConnectionsAction, organizationDetails]);

  const fetchPreview = React.useCallback(
    async (pendingConnections: IPendingConnection[], organizationDetails: ICompany) => {
      if (!organizationDetails) {
        return null;
      }

      const pendingClients = pendingConnections.map(connection => ({
        organizationId: connection.organizationId,
        companyName: connection.organizationName,
        companyId: connection.organizationCompanyId,
        isPendingConnection: true
      }));

      const previewPayload = await dispatch(
        fetchConnectionsBillingPreview({
          companyName: organizationDetails.companyName,
          companyId: Number(organizationDetails.companyId),
          organizationId: organizationDetails.aionOrganizationId,
          hiringClients: pendingClients
        })
      );

      return (previewPayload as unknown) as ConnectionsBillingPreviewPayload;
    },
    [dispatch]
  );

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setOption(PaymentOptions[(event.target as HTMLInputElement).value]);
  };

  const handleSetUpLater = () => {
    status && handleUpdateAutoConnect(false);
    handleUpdateAutoPayProperty(false, AutoConnectStatus.InitialSelectionSetupLater);
    handleClearAutoAcceptErrors();

    onClose();
  };

  const handleContinue = () => {
    const paymentMethodOnlyChosen = option === PaymentOptions.AddPaymentMethodOnly;
    const enableAutoConnectChosen = option === PaymentOptions.EnableAutoConnect;

    if (paymentMethodOnlyChosen) {
      setShowRequiresPaymentPage(true);
    } else if (enableAutoConnectChosen && pendingConnections.length) {
      setShowPaymentPage(true);
    } else {
      setShowRequiresPaymentPage(true);
    }
  };

  const handleShowError = React.useCallback(() => {
    const paymentPageShown = showRequiresPaymentPage || showPaymentPage;

    if (paymentPageShown && showErrorPopUp(billingSystemError.data)) {
      setShowError(true);
    }
  }, [billingSystemError.data, showPaymentPage, showRequiresPaymentPage]);

  React.useEffect(() => {
    fetchAllPendingConnectionsCallback();
  }, [fetchAllPendingConnectionsCallback]);

  React.useEffect(() => {
    if (billingSystemError.data) {
      handleShowError();
    }
  }, [billingSystemError.data, handleShowError]);

  return (
    <>
      <ConsentDialog
        open={open}
        loading={loading}
        handleChange={handleChange}
        handleContinue={handleContinue}
        handleSetUpLater={handleSetUpLater}
        option={option}
        incomingConnections={incomingConnections}
      />
      {organizationDetails && (
        <>
          <AddPaymentDialog
            open={showRequiresPaymentPage}
            companyId={Number(organizationDetails.companyId)}
            onClose={handleHideRequiresPaymentPage}
            onSuccess={handleAfterSuccessPaymentAdd}
          />
          <PaymentMethod
            open={showPaymentPage}
            companyId={organizationDetails.companyId}
            handleClose={handleHidePaymentPage}
            fetchPreview={fetchPreview}
            refreshPendingConnections={fetchAllPendingConnectionsCallback}
            handleApply={handleAfterSuccessPaymentAdd}
          />
          <GenericErrorModal open={showError} handleClose={handleCloseErrorDialog} />
          <SucceedPaymentSnackbar
            open={showSuccessSnackbar}
            message={
              option === PaymentOptions.EnableAutoConnect
                ? t('subscriber:consentDialog.autoConnectEnabled', 'Auto connect enabled')
                : t('subscriber:consentDialog.paymentMethodSaved', 'Payment Method is added')
            }
            onClose={() => setShowSuccessSnackbar(false)}
          />
        </>
      )}
    </>
  );
};
